import { createFileRoute } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { ArticlesListPage } from '@/features/articles/components';

interface ArticlesSearch {
  /** Filter the listing to a single category. The categories /
   *  reading-layer routes link here with this populated. */
  categoryId?: string;
  /** Filter the listing to a single tag. */
  tagId?: string;
  /** Filter the listing to a single group. Backend short-circuits to
   *  empty when the viewer can't see the group, so opaque ids passed
   *  via URL never leak group existence. */
  groupId?: string;
  /** Full-text article search. */
  q?: string;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Search-param validator. We don't use a zod schema directly because
 *  the existing routes (`login`) all use the explicit function shape
 *  for type-narrowing; matching that keeps the route surface uniform.
 *  Anything that isn't a UUID is silently dropped — TanStack Router
 *  then re-emits the URL without the bad param so the user sees a
 *  clean URL bar after a malformed link. */
function validateSearch(raw: Record<string, unknown>): ArticlesSearch {
  const out: ArticlesSearch = {};
  const c = raw.categoryId;
  if (typeof c === 'string' && UUID_RE.test(c)) out.categoryId = c;
  const t = raw.tagId;
  if (typeof t === 'string' && UUID_RE.test(t)) out.tagId = t;
  const g = raw.groupId;
  if (typeof g === 'string' && UUID_RE.test(g)) out.groupId = g;
  const q = raw.q;
  if (typeof q === 'string') {
    const normalized = q.trim().slice(0, 128);
    if (normalized.length > 0) out.q = normalized;
  }
  return out;
}

function ArticlesIndexRoute(): ReactNode {
  const { categoryId, tagId, groupId, q } = Route.useSearch();
  const props: { categoryId?: string; tagId?: string; groupId?: string; q?: string } = {};
  if (categoryId !== undefined) props.categoryId = categoryId;
  if (tagId !== undefined) props.tagId = tagId;
  if (groupId !== undefined) props.groupId = groupId;
  if (q !== undefined) props.q = q;
  return <ArticlesListPage {...props} />;
}

export const Route = createFileRoute('/articles/')({
  component: ArticlesIndexRoute,
  validateSearch,
});
